import React, { Component } from 'react';
import { Alert, Button, Modal, ProgressBar } from 'react-bootstrap';
import CloudDesignAlignmentForm from './CloudDesignAlignmentForm.js';

import './DesignLibraryPopup.css';

const CHUNK_BYTES = 2 * 1024 * 1024;
const sleep = milliseconds => new Promise(resolve => setTimeout(resolve, milliseconds));

export const designFormat = name => {
    const match = /\.([^.]+)$/.exec(String(name || '').toLowerCase());
    return match && ['ifc', 'dxf'].includes(match[1]) ? match[1] : '';
};

export const validDesignFiles = files => {
    const selected = Array.from(files || []);
    if (!selected.length) throw new Error('Choose an IFC or DXF file to upload');
    if (selected.length > 2) throw new Error('Upload at most one IFC and one DXF in a package');
    const formats = selected.map(file => designFormat(file.name));
    if (formats.some(format => !format) || new Set(formats).size !== formats.length)
        throw new Error('Choose one .ifc and/or one .dxf file');
    if (selected.some(file => !Number.isFinite(file.size) || file.size <= 0 || file.size > 1024 * 1024 * 1024))
        throw new Error('Each design file must be smaller than 1 GiB');
    return selected;
};

class DesignLibraryPopup extends Component {
    constructor(props) {
        super(props);
        this.state = { show: false, recording: '', designs: [], selectedId: '', files: [], loading: false,
            cloudAlignment: { clouds: [], revisions: [] }, busy: false, progress: 0, error: '', registrationOpen: false, placementOpen: false };
        this.pollGeneration = 0; this.alignmentGeneration = 0;
        this.fileInput = null;
        this.uploadController = null;
    }

    show = recording => {
        if (this.uploadController) this.uploadController.abort();
        ++this.pollGeneration;
        this.setState({ show: true, recording, designs: [], selectedId: '', files: [], loading: true,
            busy: false, progress: 0, error: '', registrationOpen: false, placementOpen: false }, this.refresh);
    }

    componentWillUnmount() {
        ++this.pollGeneration; ++this.alignmentGeneration; clearTimeout(this.alignmentTimer);
        if (this.uploadController) this.uploadController.abort();
    }

    close = () => {
        if (this.uploadController) this.uploadController.abort();
        ++this.pollGeneration; ++this.alignmentGeneration; clearTimeout(this.alignmentTimer);
        this.setState({ show: false, busy: false, registrationOpen: false, placementOpen: false });
    }

    endpoint = extra => 'design_job?recording=' + encodeURIComponent(this.state.recording) + (extra || '');

    readResponse = async response => {
        let data;
        try { data = await response.json(); } catch (error) { data = {}; }
        if (!response.ok) throw new Error(data.error || ('Design service returned HTTP ' + response.status));
        return data;
    }

    postJson = async body => this.readResponse(await fetch(this.endpoint(), {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body)
    }));

    refresh = async () => {
        if (!this.state.recording) return;
        const generation = this.pollGeneration;
        try {
            const data = await this.readResponse(await fetch(this.endpoint(), { cache: 'no-store' }));
            if (generation !== this.pollGeneration) return [];
            const designs = Array.isArray(data.designs) ? data.designs : [];
            const selectedId = this.state.selectedId && designs.some(item => item.id === this.state.selectedId)
                ? this.state.selectedId : (designs[0] ? designs[0].id : '');
            this.setState({ designs, selectedId, cloudAlignment: data.cloudAlignment || { clouds: [], revisions: [] }, loading: false, error: '' });
            clearTimeout(this.alignmentTimer);
            if (this.state.show && (data.cloudAlignment?.revisions || []).some(r => ['queued', 'processing'].includes(r.status))) this.alignmentTimer = setTimeout(this.refresh, 2000);
            return designs;
        } catch (error) {
            if (generation !== this.pollGeneration) return [];
            this.setState({ loading: false, error: error.message });
            return [];
        }
    }

    handleFileChange = event => {
        const files = Array.from(event.target.files || []);
        this.setState({ files, error: '' });
    }

    upload = async resumeDesign => {
        if (this.state.busy) return;
        let files;
        try {
            files = validDesignFiles(this.state.files);
            if (resumeDesign && (!resumeDesign.sourceFiles || files.length !== resumeDesign.sourceFiles.length
                || files.some(file => !resumeDesign.sourceFiles.some(source => source.format === designFormat(file.name)
                    && source.size === file.size && source.originalName === file.name))))
                throw new Error('Reselect the original files with the same names and sizes to resume this package');
        } catch (error) { this.setState({ error: error.message }); return; }
        const generation = ++this.pollGeneration;
        const controller = new AbortController();
        this.uploadController = controller;
        const recording = this.state.recording;
        const endpoint = extra => 'design_job?recording=' + encodeURIComponent(recording) + (extra || '');
        const post = body => fetch(endpoint(), { method: 'POST', signal: controller.signal,
            headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) }).then(this.readResponse);
        this.setState({ busy: true, error: '', progress: 0, registrationOpen: false, placementOpen: false });
        let designId = resumeDesign ? resumeDesign.id : '';
        const total = files.reduce((sum, file) => sum + file.size, 0);
        let uploaded = 0;
        try {
            const started = resumeDesign || await post({ action: 'uploadStart', files: files.map(file => ({ name: file.name, size: file.size })) });
            designId = started.id;
            if (generation !== this.pollGeneration) return;
            this.setState({ selectedId: designId });
            await this.refresh();
            for (const file of files) {
                const format = designFormat(file.name);
                const source = started.sourceFiles.find(item => item.format === format);
                const initialOffset = source.uploaded || 0;
                uploaded += initialOffset;
                for (let offset = initialOffset; offset < file.size; offset += CHUNK_BYTES) {
                    if (generation !== this.pollGeneration) return;
                    const chunk = file.slice(offset, Math.min(file.size, offset + CHUNK_BYTES));
                    const extra = '&action=uploadChunk&design=' + encodeURIComponent(designId)
                        + '&format=' + encodeURIComponent(format) + '&offset=' + offset;
                    await this.readResponse(await fetch(endpoint(extra), {
                        method: 'POST', signal: controller.signal, headers: { 'Content-Type': 'application/octet-stream' }, body: chunk
                    }));
                    if (generation !== this.pollGeneration) return;
                    uploaded += chunk.size;
                    this.setState({ progress: Math.floor(100 * uploaded / total) });
                }
            }
            await post({ action: 'uploadComplete', design: designId });
            const executor = await fetch('process_executor', { method: 'POST', signal: controller.signal });
            if (!executor.ok) throw new Error('Upload completed but processing could not start. Use Start import to retry');
            if (generation !== this.pollGeneration) return;
            this.setState({ files: [], progress: 100 });
            if (this.fileInput) this.fileInput.value = '';
            await this.refresh();
            if (generation === this.pollGeneration) await this.poll(designId);
        } catch (error) {
            if (generation !== this.pollGeneration || error.name === 'AbortError') return;
            await this.refresh();
            this.setState({ error: error.message + (designId ? '. Your upload is saved; reselect the original files and resume.' : ''), busy: false });
        }
    }

    poll = async designId => {
        const generation = ++this.pollGeneration;
        for (let attempt = 0; attempt < 1800 && generation === this.pollGeneration; attempt++) {
            await sleep(1500);
            if (generation !== this.pollGeneration) return;
            const designs = await this.refresh();
            const design = designs.find(item => item.id === designId);
            if (design && ['complete', 'failed', 'cancelled'].includes(design.status)) {
                this.setState({ busy: false, progress: design.status === 'complete' ? 100 : design.progress || 0,
                    error: design.status === 'failed' ? design.message || 'Design import failed' : '' });
                return;
            }
        }
        if (generation === this.pollGeneration) this.setState({ busy: false, error: 'Design import is still processing. Refresh to check its status.' });
    }

    cancel = async designId => {
        ++this.pollGeneration; ++this.alignmentGeneration; clearTimeout(this.alignmentTimer);
        if (this.uploadController) this.uploadController.abort();
        try {
            await this.postJson({ action: 'cancel', design: designId });
            this.setState({ busy: false, error: '' });
            await this.refresh();
        } catch (error) { this.setState({ error: error.message }); }
    }

    startImport = async designId => {
        this.setState({ busy: true, error: '' });
        try {
            const response = await fetch('process_executor', { method: 'POST' });
            if (!response.ok) throw new Error('Unable to start the import worker');
            await this.poll(designId);
        } catch (error) { this.setState({ busy: false, error: error.message }); }
    }

    saveAlignment = async config => {
        await this.postJson({ action: 'alignCloud', design: this.state.selectedId, ...config });
        this.setState({ placementOpen: false }); await this.refresh();

    }

    selectAlignment = async revision => {
        try { await this.postJson({ action: 'selectAlignment', revision }); await this.refresh(); }
        catch (error) { this.setState({ error: error.message }); }
    }

    selectDesign = designId => this.setState({ selectedId: designId, registrationOpen: false, placementOpen: false, error: '' });

    render() {
        const selected = this.state.designs.find(item => item.id === this.state.selectedId);
        const showRegistration = false;
        return (
            <Modal show={this.state.show} onHide={this.close} dialogClassName="design-library-modal" centered>
                <Modal.Header closeButton><Modal.Title>Engineering designs</Modal.Title></Modal.Header>
                <Modal.Body>
                    {this.state.error && <Alert variant="danger" role="alert">{this.state.error}</Alert>}
                    <p className="design-library-intro">Upload IFC and/or DXF files for this recording. Designs retain their native coordinates; align the point cloud to the chosen design reference.</p>
                    <div className="design-library-upload">
                        <input aria-label="IFC or DXF design files" type="file" multiple accept=".ifc,.dxf" onChange={this.handleFileChange} disabled={this.state.busy}
                            ref={input => { this.fileInput = input; }} />
                        <Button onClick={() => this.upload()} disabled={this.state.busy || this.state.files.length === 0}>Upload and import</Button>
                    </div>
                    {this.state.files.length > 0 && <div className="design-library-file-names">{this.state.files.map(file => file.name).join(' · ')}</div>}
                    {this.state.busy && <div className="design-library-progress"><ProgressBar now={this.state.progress} label={this.state.progress + '%'} />
                        {selected && selected.status && <span>{selected.message || selected.status}</span>}
                    </div>}
                    <div className="design-library-list-heading">
                        <h3>Uploaded packages</h3>
                        <Button size="sm" variant="outline-secondary" onClick={this.refresh} disabled={this.state.loading}>Refresh</Button>
                    </div>
                    {this.state.loading ? <p>Loading designs…</p> : this.state.designs.length === 0
                        ? <p>No IFC or DXF designs have been uploaded for this recording.</p>
                        : <div className="design-library-list">{this.state.designs.map(design => (
                            <button type="button" key={design.id} className={'design-library-row' + (design.id === this.state.selectedId ? ' selected' : '')} onClick={() => this.selectDesign(design.id)}>
                                <strong>{design.name}</strong>
                                <span>{design.status === 'complete' ? 'Imported · fixed native coordinates'
                                    : (design.message || design.status) + (design.progress ? ' · ' + design.progress + '%' : '')}</span>
                                <small>{(design.sourceFiles || []).map(file => file.name).join(' · ')}</small>
                            </button>
                        ))}</div>}
                    {selected && selected.status === 'complete' && !showRegistration && !this.state.placementOpen && (
                        <section className="design-library-details">
                            <h3>{selected.name}</h3>
                            <div className="design-library-formats">{(selected.formats || []).map(format => (
                                <div key={format.format}>
                                    <strong>{format.format.toUpperCase()}</strong> · {((format.counts && format.counts.features) || 0).toLocaleString()} features · {((format.counts && format.counts.triangles) || 0).toLocaleString()} triangles
                                    {format.unsupportedCount > 0 && <span className="design-library-warning"> · {format.unsupportedCount} unsupported geometry item(s)</span>}
                                </div>
                            ))}</div>
                            <div className="design-library-sources">{(selected.sourceFiles || []).map(file => (
                                <a key={file.path} href={this.endpoint('&design=' + encodeURIComponent(selected.id) + '&action=download&path=' + encodeURIComponent(file.path))}>{file.name} · download original</a>
                            ))}</div>
                            <p>The design stays fixed. Create or select a cloud alignment for this reference.</p>
                            <Button onClick={() => this.setState({ placementOpen: true })} disabled={(this.state.cloudAlignment.revisions || []).some(r => ['queued', 'processing'].includes(r.status))}>Align point cloud to design</Button>
                        </section>
                    )}
                    {selected && this.state.placementOpen && <CloudDesignAlignmentForm key={selected.id}
                        value={(this.state.cloudAlignment.revisions || []).filter(r => r.design === selected.id).sort((a,b) => b.config.createdAt-a.config.createdAt)[0]?.config}
                        recording={this.state.recording} design={selected.id} clouds={this.state.cloudAlignment.clouds} hasDem={this.state.cloudAlignment.hasDem}
                        onSave={this.saveAlignment} onCancel={() => this.setState({ placementOpen: false })} />}
                    <section className="design-library-details"><h3>Cloud alignment views</h3>
                        {(this.state.cloudAlignment.revisions || []).filter(r => !selected || r.design === selected.id).sort((a,b) => b.config.createdAt-a.config.createdAt).map(r => <div key={r.revision}>
                            <p>{r.message || r.status} {r.progress ? ' · ' + r.progress + '%' : ''}</p>
                            {r.status === 'complete' && <Button size="sm" onClick={() => this.selectAlignment(r.revision)} disabled={this.state.cloudAlignment.current?.revision === r.revision}>{this.state.cloudAlignment.current?.revision === r.revision ? 'Active reference' : 'Use this cloud view'}</Button>}
                            {(r.clouds || []).map(c => <div key={c.las}><a download href={'maps/' + encodeURIComponent(this.state.recording) + '/' + c.las}>Download aligned {c.source.split('/').pop()}</a></div>)}
                            {r.status === 'complete' && <a href={'maps/' + encodeURIComponent(this.state.recording) + '/design-alignments/' + r.revision + '/alignment.json'}> Transformation record and export paths</a>}
                        </div>)}
                        {this.state.cloudAlignment.current && <Button variant="outline-secondary" onClick={() => this.selectAlignment('')}>Return to original cloud coordinates</Button>}
                        {this.props.onView && <Button onClick={() => { this.props.onView(this.state.recording); this.close(); }}>View with recording</Button>}
                    </section>
                    {selected && selected.status === 'uploading' && !this.state.busy && <Button onClick={() => this.upload(selected)} disabled={!this.state.files.length}>Resume upload</Button>}
                    {selected && selected.status === 'queued' && !this.state.busy && <Button onClick={() => this.startImport(selected.id)}>Start import</Button>}
                    {selected && ['uploading', 'queued', 'processing'].includes(selected.status)
                        && <Button variant="outline-danger" onClick={() => this.cancel(selected.id)}>Cancel import</Button>}
                </Modal.Body>
                <Modal.Footer><Button variant="secondary" onClick={this.close}>Close</Button></Modal.Footer>
            </Modal>
        );
    }
}

export default DesignLibraryPopup;
